import React, { memo, useState, useEffect } from "react";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { validators, formats } from "investira.sdk";
import {
  LoadingButton,
  Button,
  Icon,
  Skeleton,
  Stack,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Badge,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  ButtonBase,
} from "investira.react.components";
import { PessoaImage } from "../../components/molecules";
import services from "../../services";
import withResponseHandling from "../../hoc/withResponseHandling";
import { useCarteirasContext } from "../../contexts/CarteirasContext";
import PosicaoAbertaChart from "./PosicaoAbertaChart";
import { abertaPendencia } from "../../services/posicoes";
import { PATHS } from "../../enums";

const Abertura = memo((props) => {
  const {
    carteiraTipoId,
    carteiraId,
    dataPosicao,
    datasPosicoes,
    hasFND,
    responseErrorHandling,
  } = props;

  const { actions } = useCarteirasContext();

  const [isLoading, setIsLoading] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [posicao, setPosicao] = useState({});
  const [posicaoAberta, setPosicaoAberta] = useState([]);
  const [posicaoPendencias, setPosicaoPendencias] = useState([]);
  const [open, setOpen] = useState(false);

  function getPosicao(pDataPosicao, pDatasPosicoes) {
    return pDatasPosicoes.filter((xItem) => {
      return dayjs(xItem.data).isSame(dayjs(pDataPosicao));
    });
  }

  function handleOpen(pCarteiraId, pDataPosicao) {
    setIsRequesting(true);
    services.posicoes.open(
      {
        carteira_id: pCarteiraId,
        data: pDataPosicao,
      },
      (rRes) => {
        setPosicao((prevState) => {
          return { ...prevState, aberta: true };
        });

        actions.readDatasPosicoes(pCarteiraId, (rRes) => {
          actions.handleSetDatasPosicoes(rRes.data);
        });
      },
      (rErr) => {
        responseErrorHandling(rErr);
      },
      () => {
        setIsRequesting(false);
      }
    );
  }

  const handleDialogOpen = () => {
    setOpen(true);
  };

  const handleDialogClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (!validators.isEmpty(carteiraId) && !validators.isEmpty(datasPosicoes)) {
      const xPosicao = getPosicao(dataPosicao, datasPosicoes)[0];

      !validators.isNull(xPosicao) && setPosicao(xPosicao || {});
    }
  }, [carteiraId, dataPosicao, datasPosicoes]);

  useEffect(() => {
    if (posicao.aberta) {
      services.posicoes.abertaResumo(
        {
          carteira_id: carteiraId,
          data: dataPosicao,
        },
        (rRes) => {
          setPosicaoAberta(rRes.data);
        },
        (rErr) => {
          responseErrorHandling(rErr);
        }
      );
    }

    if (posicao.aberta_pendencia) {
      services.posicoes.abertaPendencia(
        {
          carteira_id: carteiraId,
          data: dataPosicao,
        },
        (rRes) => {
          setPosicaoPendencias(rRes.data);
        },
        (rErr) => {
          responseErrorHandling(rErr);
        }
      );
    }
  }, [posicao, carteiraId, dataPosicao]);

  if (!hasFND || carteiraTipoId === "T") {
    return null;
  }

  if (isLoading) {
    return <Skeleton variant="rounded" width="150px" height={30} />;
  }

  if (validators.isEmpty(posicao)) {
    return null;
  }

  if (posicao?.aberta === false) {
    return (
      <>
        <Stack direction="row" spacing={1} alignItems="center">
          <LoadingButton
            variant="contained"
            color="primary"
            startIcon={<Icon color="secondary" iconName="fund" size={16} />}
            size="small"
            sx={{ px: 2 }}
            onClick={() => handleOpen(carteiraId, dataPosicao)}
            disabled={isRequesting}
            loading={isRequesting}
          >
            Abrir Posição
          </LoadingButton>
        </Stack>
        <Divider orientation="vertical" flexItem />
      </>
    );
  }

  return (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        <Badge color="error" badgeContent={posicaoPendencias.length} max={99}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Icon iconName="fund" size={14} />}
            size="small"
            sx={{ px: 2 }}
            onClick={handleDialogOpen}
            disabled={isRequesting}
          >
            Posição Aberta
          </Button>
        </Badge>
        <LoadingButton
          variant="contained"
          color="primary"
          startIcon={
            <Icon color="secondary" iconName="synchronize" size={16} />
          }
          size="small"
          sx={{ px: 2 }}
          onClick={() => handleOpen(carteiraId, dataPosicao)}
          disabled={isRequesting}
          loading={isRequesting}
        >
          Reabrir
        </LoadingButton>
      </Stack>
      <Divider orientation="vertical" flexItem />
      <Dialog open={open} maxWidth="xl" fullWidth onClose={handleDialogClose}>
        <DialogTitle>Resumo - Posição Aberta</DialogTitle>
        <DialogContent>
          <Stack direction="column" alignItems="center" spacing={2}>
            <Stack sx={{ flex: 1, minWidth: 0 }}>
              <PosicaoAbertaChart data={posicaoAberta} />
            </Stack>
            {!validators.isEmpty(posicaoPendencias) && (
              <>
                <Divider flexItem />
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    color="textPrimary"
                    variant="subtitle1"
                    sx={{ fontWeight: "bold" }}
                  >
                    Pendências
                  </Typography>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell></TableCell>
                        <TableCell>Ativo</TableCell>
                        <TableCell>ISIN</TableCell>
                        <TableCell>Tipo</TableCell>
                        {/* <TableCell align="right">PU</TableCell>
                        <TableCell align="right">Quantidade</TableCell> */}
                        <TableCell align="right">Financeiro</TableCell>
                        <TableCell>Emissor</TableCell>
                        <TableCell>PL</TableCell>
                        <TableCell>Posição</TableCell>
                        <TableCell>Setor</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {posicaoPendencias.map((xItem) => {
                        const xPath = "/empresas";
                        return (
                          <TableRow>
                            <TableCell>
                              <ButtonBase
                                component={Link}
                                to={`${xPath}/${xItem.logo_pessoa_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <PessoaImage
                                  logoPessoaId={xItem.logo_pessoa_id}
                                  alt={String(xItem.logo_pessoa_id)}
                                  size={36}
                                />
                              </ButtonBase>
                            </TableCell>
                            <TableCell>
                              <Typography
                                color="primary"
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                                component={Link}
                                to={`/ativos${
                                  PATHS[xItem.mercado.toLowerCase()].path
                                }/${xItem.ifeed_ativo_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {xItem.ativo}
                              </Typography>
                            </TableCell>
                            <TableCell>{xItem.isin}</TableCell>
                            <TableCell>{xItem.ativo_tipo}</TableCell>
                            {/* <TableCell align="right">
                              {formats.formatNumber(xItem.pu, 2, true, true)}
                            </TableCell>
                            <TableCell align="right">
                              {xItem.quantidade}
                            </TableCell> */}
                            <TableCell align="right">
                              {formats.formatNumber(
                                xItem.financeiro,
                                2,
                                true,
                                true
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={xItem.semEmissor ? "Ausente" : "Ok"}
                                color={xItem.semEmissor ? "error" : "primary"}
                                sx={{ cursor: "help" }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={xItem.semPL ? "Ausente" : "Ok"}
                                color={xItem.semPL ? "error" : "primary"}
                                sx={{ cursor: "help" }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={xItem.semPosicao ? "Ausente" : "Ok"}
                                color={xItem.semPosicao ? "error" : "primary"}
                                sx={{ cursor: "help" }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={xItem.semSetor ? "Ausente" : "Ok"}
                                color={xItem.semSetor ? "error" : "primary"}
                                sx={{ cursor: "help" }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Stack>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            type="button"
            color="primary"
            onClick={handleDialogClose}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default withResponseHandling(Abertura);
